import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import omit from 'lodash/omit';
import set from 'lodash/set';

import * as LeadStateSelectors from '@/selectors/leadStateSelectors';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import type { DefaultRootState } from '@/types';
import { LeadModel } from '@/typings/models/Lead';

type Transformer = (value: any, state: DefaultRootState, cloned: any) => void;

const fieldTransformers: Record<string, Transformer> = {
  lead_state_id: (value, state, cloned) => {
    const leadStates = LeadStateSelectors.selectStates(state);
    const leadStateObject = leadStates.find(leadState => leadState.id === value);
    if (leadStateObject) {
      set(cloned, 'lead_state', leadStateObject);
    }
  },

  priority: (value, state, cloned) => {
    if (value !== null && value !== undefined) {
      const priorities = PreferencesSelectors.selectPriorityList(state);
      const priorityObject = priorities.find(priority => priority.value === value);
      set(cloned, 'priority', priorityObject || value);
    }
  },

  // assignee_id: (value, state, cloned) => {
  //   const assignees = LeadSelectors.selectLeadRawAssignees(state);
  //   const assigneeObject = assignees.find(assignee => assignee.id === value);
  //   if (assigneeObject) {
  //     set(cloned, 'assignee', assigneeObject);
  //   }
  // }
};

export const prepareLeadForLocalEdit = (payload: any, state: DefaultRootState) => {
  const cloned = cloneDeep(payload);

  for (const [key, value] of Object.entries(cloned)) {
    const transformer = fieldTransformers[key];
    if (transformer) {
      transformer(value, state, cloned);
    } else {
      set(cloned, key, value);
    }
  }

  return cloned;
};

// `leads/edit` payload keys that never land on the lead object.
export const LEAD_EDIT_META_KEYS = ['leadId', 'applyLate', 'leadOverride', 'query', 'assignee', 'fetchDetails'];

// Payload key → store path the optimistic update writes (see fieldTransformers above).
const LOCAL_EDIT_PATHS: Record<string, string> = {
  lead_state_id: 'lead_state',
};

export const getLocalEditPaths = (payload: Record<string, any>): string[] =>
  Object.keys(omit(payload, LEAD_EDIT_META_KEYS)).map((key) => LOCAL_EDIT_PATHS[key] || key);

export type RecentLocalEdit = {
  paths: string[];
  // `modified` of the server-confirmed lead; unset while the PATCH is in flight.
  modified?: string;
  expiresAt: number;
};

const toTimestamp = (value?: string) => {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
};

export const isNewerThanLocalEdit = (edit: RecentLocalEdit, incoming?: Pick<LeadModel, 'modified'>) => {
  const editTime = toTimestamp(edit.modified);
  const incomingTime = toTimestamp(incoming?.modified);
  return editTime !== null && incomingTime !== null && incomingTime > editTime;
};

/**
 * The `lead_updated` websocket echo of this client's own PATCH is emitted from a queued
 * backend listener, so it lands after the PATCH response and can still carry the previous
 * `lead_state` (stale relation, fresh attributes). Re-apply the locally edited paths from
 * the store while the edit is recent and the payload isn't newer than the confirmed lead.
 */
export const reconcileWebsocketLead = <T extends LeadModel>(
  incoming: T,
  stored: LeadModel | undefined,
  edit: RecentLocalEdit | undefined,
  now = Date.now(),
): T => {
  if (!incoming || !stored || !edit) return incoming;
  if (now >= edit.expiresAt) return incoming;
  if (isNewerThanLocalEdit(edit, incoming)) return incoming;

  const reconciled = cloneDeep(incoming);
  edit.paths.forEach((path) => {
    const value = get(stored, path);
    if (value !== undefined) set(reconciled, path, value);
  });
  return reconciled;
};


type AnyObject = Record<string, any>;

export function formatCustomerDotNotationObject<T extends AnyObject>(
  input: T
): AnyObject {
  const result: AnyObject = {};

  for (const [key, value] of Object.entries(input)) {
    if (!key.startsWith('customer.')) {
      result[key] = value;
      continue;
    }

    const path = key.split('.');
    let current = result;

    for (let i = 0; i < path.length; i++) {
      const part = path[i];

      if (i === path.length - 1) {
        current[part] = value;
      } else {
        current[part] ??= {};
        current = current[part];
      }
    }
  }

  return result;
}


export const getLeadTitle = (lead: LeadModel) => {
  return (
    lead.title ||
    lead?.product_group?.label ||
    lead?.latest_appointment?.branch_service?.service_type?.name ||
    lead?.latest_appointment?.branch_service?.name || ' - '
  );
}
